(() => {
  if (customElements.get('ug-collection-filters')) return;
  customElements.define('ug-collection-filters', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.initialPath = location.pathname;
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.container = document.getElementById('ProductGridContainer');
      if (!this.container) return;
      const grid = this.container.querySelector('#product-grid');
      this.gridClass = grid?.className || 'grid product-grid grid--2-col-tablet-down grid--4-col-desktop';
      this.limit = Number(grid?.dataset.ugPageSize) || 60;
      this.status = this.querySelector('[data-ug-status]');
      this.retry = this.querySelector('[data-ug-retry]');
      this.form = this.querySelector('[data-ug-filters]');
      this.brandSelect = this.querySelector('[data-ug-brand-select]');
      this.modelSelect = this.querySelector('[data-ug-model]');
      this.sizeSelect = this.querySelector('[data-ug-size]');
      this.sortSelect = this.querySelector('[name="sort_by"]');
      this.form.addEventListener('submit', event => event.preventDefault(), opts);
      this.form.addEventListener('change', event => {
        this.brand = this.brandSelect.value;
        if (event.target === this.brandSelect) { this.modelSelect.value = ''; this.sizeSelect.value = ''; }
        if (event.target === this.modelSelect) this.sizeSelect.value = '';
        this.model = this.modelSelect.value;
        this.size = this.sizeSelect.value;
        this.sort = this.sortSelect.value;
        this.page = 1;
        this.updateURL();
        this.apply();
      }, opts);
      this.retry.addEventListener('click', () => this.apply(), opts);
      window.addEventListener('popstate', () => { this.readURL(); this.apply(); }, opts);
      document.querySelectorAll('[data-season-choice]').forEach(link => link.addEventListener('click', event => {
        if(event.metaKey||event.ctrlKey||event.shiftKey||event.altKey)return;
        event.preventDefault();this.season=link.dataset.seasonChoice;this.brand='';this.model='';this.size='';this.page=1;
        this.updateURL();this.apply();this.scrollIntoView({block:'start'});
      },opts));
      this.readURL();
      if (this.season || this.brand || this.model || this.size) this.apply();
      else this.loadMetadata().catch(error => console.warn('UG filter metadata:', error.message));
    }
    readURL() {
      const params = new URL(location.href).searchParams;
      this.season = ['autumn','winter'].includes(params.get('ug_season')) ? params.get('ug_season') : '';
      this.syncSeason();
      this.brand = params.get('ug_brand') || '';
      this.size = params.get('ug_size') || '';
      this.model = params.get('ug_model') || (location.pathname === this.initialPath ? this.dataset.initialModel : '') || '';
      this.sort = params.get('sort_by') || 'best-selling';
      this.page = Math.max(1, Math.floor(Number(params.get('ug_page')) || 1));
      if (this.sortSelect) this.sortSelect.value = this.sort;
      this.modelSelect.value = this.model;
      this.brandSelect.value = this.brand;
      this.sizeSelect.value = this.size;
    }
    updateURL() {
      const url = new URL(location.href);
      url.pathname = new URL(this.dataset.feed, location.origin).pathname;
      for (const [name, value] of [['ug_season', this.season], ['ug_brand', this.brand], ['ug_model', this.model], ['ug_size', this.size], ['sort_by', this.sort], ['ug_page', this.page > 1 ? this.page : '']]) {
        if (value) url.searchParams.set(name, value); else url.searchParams.delete(name);
      }
      url.searchParams.delete('page');
      url.hash = 'ug-catalog-brands';
      history.pushState({}, '', url);
    }
    syncSeason() {
      document.querySelectorAll('[data-season-choice]').forEach(link=>link.setAttribute('aria-current',String(link.dataset.seasonChoice===this.season)));
    }
    modelLabel(model) {
      if (model === 'X' && this.dataset.seasonPuma) return this.dataset.seasonPuma;
      const label = window.ugModelLabel?.(model) || model;
      return this.dataset.seasonUgg ? this.dataset.seasonUgg.replace('__MODEL__', label) : label;
    }
    setOptions(select, values, selected, label = value => value) {
      const options = [...new Set(values.filter(Boolean))].sort((a,b) => label(a).localeCompare(label(b), 'sr', {numeric:true}));
      while (select.options.length > 1) select.remove(1);
      options.forEach(value => select.add(new Option(label(value), value)));
      select.value = options.includes(selected) ? selected : '';
      return select.value;
    }
    updateModels(values) {
      this.model = this.setOptions(this.modelSelect, values, this.model, value => this.modelLabel(value));
    }
    updateFilters(records) {
      if(this.season) records=records.filter(record=>record.season===this.season);
      this.brand = this.setOptions(this.brandSelect, records.map(record => record.brand), this.brand);
      const brandRecords = records.filter(record => !this.brand || record.brand === this.brand);
      this.updateModels(brandRecords.flatMap(record => record.models));
      const modelRecords = brandRecords.filter(record => !this.model || record.models.includes(this.model));
      this.size = this.setOptions(this.sizeSelect, modelRecords.flatMap(record => record.sizes || []), this.size);
    }
    async loadMetadata() {
      const records = new Map(), visited = new Set();
      let next = new URL(this.dataset.feed, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id') || (window.Shopify?.theme?.role === 'unpublished' ? String(window.Shopify.theme.id) : '');
      while (next) {
        if (next.origin !== location.origin) throw new Error('Invalid feed origin');
        next.searchParams.set('section_id','ug-collection-filter-meta');
        if (preview) { next.searchParams.set('preview_theme_id',preview); next.searchParams.set('_fd','0'); next.searchParams.set('pb','0'); }
        if (visited.has(next.href)) throw new Error('Repeated feed page');
        visited.add(next.href);
        const response = await fetch(next.href,{signal:AbortSignal.any([this.abort.signal,AbortSignal.timeout(20000)])});
        if (!response.ok) throw new Error('Collection unavailable');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const meta = JSON.parse(doc.querySelector('[data-ug-filter-meta]').textContent);
        meta.products.forEach(record => records.set(String(record.id),record));
        next = meta.next ? new URL(meta.next,next) : null;
      }
      if (!this.isConnected || this.records) return;
      const all = [...records.values()];
      this.updateFilters(all);
    }
    async load() {
      if (this.records) return;
      if (this.pending) return this.pending;
      this.pending = (async () => {
        const records = new Map(), visited = new Set();
        let next = new URL(this.dataset.feed, location.origin);
        const preview = new URL(location.href).searchParams.get('preview_theme_id') || (window.Shopify?.theme?.role === 'unpublished' ? String(window.Shopify.theme.id) : '');
        while (next) {
          if (next.origin !== location.origin) throw new Error('Invalid feed origin');
          next.searchParams.set('section_id', 'ug-collection-filter-feed');
          next.searchParams.set('sort_by', 'best-selling');
          if (preview) { next.searchParams.set('preview_theme_id',preview); next.searchParams.set('_fd','0'); next.searchParams.set('pb','0'); }
          if (visited.has(next.href)) throw new Error('Repeated feed page');
          visited.add(next.href);
          const response = await fetch(next.href, { signal: AbortSignal.any([this.abort.signal, AbortSignal.timeout(20000)]) });
          if (!response.ok) throw new Error('Collection unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const root = doc.querySelector('[data-ug-filter-feed]');
          if (!root) throw new Error('Missing collection feed');
          root.querySelectorAll('template[data-ug-filter-card]').forEach(template => {
            if (!template.content.firstElementChild) return;
            records.set(template.dataset.id, {
              id: template.dataset.id, season: template.dataset.season, brand: (template.dataset.brand || '').trim(),
              models: (template.dataset.models || '').split('|').filter(Boolean),
              sizes: [...new Set((template.dataset.sizes || '').split('|').filter(Boolean))],
              price: Number(template.dataset.price), card: template.content.firstElementChild
            });
          });
          const link = root.querySelector('[data-ug-filter-next]');
          next = link ? new URL(link.getAttribute('href'), next) : null;
        }
        this.records = [...records.values()];
      })();
      try { await this.pending; } finally { this.pending = null; }
    }
    async apply() {
      const revision = this.revision = (this.revision || 0) + 1;
      this.retry.hidden = true;
      this.status.textContent = this.dataset.loading;
      this.syncSeason();
      this.container.setAttribute('aria-busy', 'true');
      try {
        await this.load();
        if (!this.isConnected || revision !== this.revision) return;
        this.render();
      } catch (error) {
        if (!this.isConnected || revision !== this.revision) return;
        console.warn('UG collection filters:', error.message);
        this.status.textContent = this.dataset.error;
        this.retry.hidden = false;
      } finally {
        if (revision === this.revision) this.container.removeAttribute('aria-busy');
      }
    }
    render() {
      this.updateFilters(this.records);
      const records = this.records.filter(record => (!this.season || record.season === this.season) && (!this.brand || record.brand === this.brand) && (!this.model || record.models.includes(this.model)) && (!this.size || record.sizes.includes(this.size)));
      if (this.sort === 'price-ascending') records.sort((a,b) => a.price-b.price);
      if (this.sort === 'price-descending') records.sort((a,b) => b.price-a.price);
      const pages = Math.max(1, Math.ceil(records.length / this.limit));
      this.page = Math.min(pages, Math.max(1, this.page));
      const wrap = document.createElement('div'); wrap.className = 'collection page-width';
      const grid = document.createElement('ul'); grid.id = 'product-grid'; grid.className = this.gridClass;
      grid.dataset.ugShuffled = 'true';
      grid.append(...records.slice((this.page-1)*this.limit, this.page*this.limit).map(record => record.card.cloneNode(true)));
      wrap.append(grid);
      if (pages > 1) {
        const nav = document.createElement('nav'); nav.className = 'ugb-filter-pagination';
        const page = document.createElement('span'); page.textContent = this.page + ' / ' + pages;
        const button = (step, label, disabled) => {
          const el = document.createElement('button'); el.type = 'button'; el.textContent = label; el.disabled = disabled;
          el.addEventListener('click', () => { this.page += step; this.updateURL(); this.render(); this.container.scrollIntoView({block:'start'}); });
          return el;
        };
        nav.append(button(-1, this.dataset.previous, this.page === 1), page, button(1, this.dataset.next, this.page === pages)); wrap.append(nav);
      }
      this.container.replaceChildren(wrap);
      this.container.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
      this.container.hidden = false;
      this.status.textContent = records.length ? this.dataset.count.replace('__COUNT__', records.length) : this.dataset.empty;
      window.ugProductFit?.scan();
    }
    disconnectedCallback() { this.abort?.abort(); this.ready = false; }
  });
})();


